import type { TextSize } from "@homeinstead/schemas";
import type { PortableTextComponents } from "@portabletext/react";
import type {
  CreateRichTextComponentsOptions,
  RichTextComponentsFactory,
} from "./types";

import { resolveTextSizeClass } from "@homeinstead/schemas";
import { cn } from "../../utils/classnames";
import { AccentText } from "./AccentText";
import { createDynamicPlaceholderMark } from "./create-dynamic-placeholder-mark";
import { createLinkMark } from "./create-link-mark";
import { RichTextAccordion } from "./RichTextAccordion";
import { RichTextColumns } from "./RichTextColumns";

const BOLD_TEXT_SIZES = new Set<TextSize>([
  "xl",
  "1xl",
  "2xl",
  "3xl",
  "4xl",
  "5xl",
]);

type CreateDefaultMarkComponentsOptions = Pick<
  CreateRichTextComponentsOptions,
  | "activePlaceholderKeys"
  | "components"
  | "includeAccordion"
  | "includeColumns"
  | "headingFont"
  | "onLinkClick"
  | "placeholders"
> & {
  createRichTextComponents: RichTextComponentsFactory;
};

function createDefaultMarkComponents({
  activePlaceholderKeys = [],
  components,
  createRichTextComponents,
  includeAccordion,
  includeColumns,
  headingFont,
  onLinkClick,
  placeholders,
}: CreateDefaultMarkComponentsOptions): PortableTextComponents["marks"] {
  return {
    accentText: ({ children, value }) => (
      <AccentText value={value}>{children}</AccentText>
    ),
    caregiverLeadDrawer: ({ children, value }) => {
      const variant = value?.variant ?? "plain";
      return (
        <span
          className={cn(
            "inline-flex cursor-pointer items-center justify-center rounded-lg font-jakarta font-semibold text-base",
            {
              "border-2 border-transparent bg-friendly-base px-6 py-2 text-new-beginnings-base":
                variant === "friendly",
              "border-2 border-transparent bg-new-beginnings-base px-6 py-2 text-support-base":
                variant === "primary",
              "underline hover:no-underline": variant === "plain",
            },
          )}
        >
          {children}
        </span>
      );
    },
    dynamicPlaceholder: createDynamicPlaceholderMark({
      activePlaceholderKeys,
      components,
      createRichTextComponents,
      headingFont,
      includeAccordion,
      includeColumns,
      onLinkClick,
      placeholders,
    }),
    link: createLinkMark({ onLinkClick }),
    textSize: ({ children, value }) => (
      <span
        className={cn(resolveTextSizeClass(value?.size), {
          "font-bold":
            headingFont === "jakarta" &&
            BOLD_TEXT_SIZES.has(value?.size as TextSize),
        })}
      >
        {children}
      </span>
    ),
  };
}

function createDefaultTypeComponents({
  activePlaceholderKeys = [],
  components,
  createRichTextComponents,
  includeAccordion = true,
  includeColumns = true,
  headingFont,
  onLinkClick,
  placeholders,
}: CreateDefaultMarkComponentsOptions): PortableTextComponents["types"] {
  return {
    hardBreak: () => <br />,
    ...(includeAccordion
      ? {
          richTextAccordion: ({ value }) => (
            <RichTextAccordion
              activePlaceholderKeys={activePlaceholderKeys}
              components={components}
              createRichTextComponents={createRichTextComponents}
              headingFont={headingFont}
              onLinkClick={onLinkClick}
              placeholders={placeholders}
              value={value}
            />
          ),
        }
      : {}),
    ...(includeColumns
      ? {
          richTextColumns: ({ value }) => (
            <RichTextColumns
              activePlaceholderKeys={activePlaceholderKeys}
              components={components}
              createRichTextComponents={createRichTextComponents}
              headingFont={headingFont}
              onLinkClick={onLinkClick}
              placeholders={placeholders}
              value={value}
            />
          ),
        }
      : {}),
  };
}

export { createDefaultMarkComponents, createDefaultTypeComponents };
